import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from '@mlm/ui';
import { membershipHierarchy, sortMembershipsByHierarchy } from '@mlm/shared';
import {
  createMembership,
  listAllMemberships,
  updateMembership,
  type DailyCapGroup,
  type Membership,
} from '../lib/api';
import { HARD_CAP_PLAN_CODES, planDisplayName, SUPPORTED_PLAN_CODES } from '../components/compplan';
import { RecruitRulesCard } from '../components/memberships/recruit-rules-card';
import { ReplicatedPackagesCard } from '../components/memberships/replicated-packages-card';

// Superseded by Campaign Links (Replicated Pages → per-page Packages) — flip to
// restore the per-tier "Packages for replicated link" matrix.
const SHOW_PACKAGE_LINKS = false;

/**
 * Membership tier CRUD. Tiers carry only their identity (code, name) and a
 * baseline discount percentage. Price comes from the tier's kit product;
 * PV comes from each product. Per-tier comp-plan config lives in the
 * separate ComPlan editor.
 */
export function MembershipsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['admin-memberships'],
    queryFn: listAllMemberships,
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const create = useMutation({
    mutationFn: createMembership,
    meta: { successMessage: 'Membership created.' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
      setCreating(false);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Membership> }) =>
      updateMembership(id, patch),
    meta: { successMessage: 'Membership saved.' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
      setEditing(null);
    },
  });

  const sorted = sortMembershipsByHierarchy(query.data ?? []);
  const archivedCount = sorted.filter((m) => !m.active).length;
  const visible = showArchived ? sorted : sorted.filter((m) => m.active);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Membership tiers</h1>
        <Button onClick={() => setCreating(true)}>+ New tier</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Tiers identify membership classes and carry a baseline discount. Price comes from the
        tier's kit product (Products page); PV comes from each product. Per-tier comp plan
        rates live in the ComPlan editor.
      </p>

      {creating && (
        <MembershipForm
          submitting={create.isPending}
          onSubmit={(input) =>
            create.mutate({
              code: input.code,
              name: input.name,
              config: input.config,
            })
          }
          onCancel={() => setCreating(false)}
          error={create.error}
        />
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Tiers</CardTitle>
          {archivedCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tiers yet.</p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              All tiers are archived. Use “Show archived” to view them.
            </p>
          ) : (
            <ul className="divide-y">
              {visible.map((m) => (
                <li key={m.id} className="py-3">
                  {editing === m.id ? (
                    <MembershipForm
                      membership={m}
                      submitting={update.isPending}
                      onSubmit={(input) =>
                        update.mutate({
                          id: m.id,
                          patch: {
                            name: input.name,
                            discountPct: input.discountPct,
                            config: input.config,
                          } as Partial<Membership>,
                        })
                      }
                      onCancel={() => setEditing(null)}
                      error={update.error}
                    />
                  ) : (
                    <MembershipRow
                      membership={m}
                      onEdit={() => setEditing(m.id)}
                      onToggleActive={() =>
                        update.mutate({
                          id: m.id,
                          patch: { active: m.active ? 0 : 1 } as Partial<Membership>,
                        })
                      }
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {!query.isLoading && sorted.length > 0 && <RecruitRulesCard memberships={sorted} />}
      {SHOW_PACKAGE_LINKS && !query.isLoading && sorted.length > 0 && (
        <ReplicatedPackagesCard memberships={sorted} />
      )}
    </div>
  );
}

function MembershipRow({
  membership,
  onEdit,
  onToggleActive,
}: {
  membership: Membership;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  const tier = membershipHierarchy(membership.config);
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium tabular-nums"
            title="Tier rank (hierarchy)"
          >
            {tier === null ? 'Tier —' : `Tier ${tier}`}
          </span>
          <span className="font-medium truncate">{membership.name}</span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
            {membership.code}
          </span>
          {!membership.active && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              Archived
            </span>
          )}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onEdit}>
        Edit
      </Button>
      <Button variant="outline" size="sm" onClick={onToggleActive}>
        {membership.active ? 'Archive' : 'Restore'}
      </Button>
    </div>
  );
}

function MembershipForm({
  membership,
  submitting,
  onSubmit,
  onCancel,
  error,
}: {
  membership?: Membership;
  submitting: boolean;
  onSubmit: (input: {
    code: string;
    name: string;
    discountPct: string;
    config?: Record<string, unknown>;
  }) => void;
  onCancel: () => void;
  error: unknown;
}) {
  const [code, setCode] = useState(membership?.code ?? '');
  const [name, setName] = useState(membership?.name ?? '');
  // Discount is edited in the dedicated Discounts tab; preserved here on edit.
  const [discountPct] = useState(membership?.discountPct ?? '0');
  const storedHierarchy = membership?.config?.membership_hierarchy;
  const [hierarchy, setHierarchy] = useState<string>(
    typeof storedHierarchy === 'number' ? String(storedHierarchy) : '',
  );
  const [fsEligible, setFsEligible] = useState<boolean>(
    membership?.config?.fs_eligible === true,
  );
  const [dailyCaps, setDailyCaps] = useState<Record<string, string>>(() => {
    const stored = (membership?.config?.daily_caps ?? {}) as Record<string, number>;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(stored)) out[k] = String(v);
    return out;
  });
  // Per-tier daily income cap GROUPS (this tier's own groups: plans + limit).
  const [dailyCapGroups, setDailyCapGroups] = useState<DailyCapGroup[]>(
    () => (membership?.config?.daily_cap_groups as DailyCapGroup[] | undefined) ?? [],
  );
  // Per-tier LIFETIME hard caps (per plan; includes binary — it self-threads).
  const [hardCaps, setHardCaps] = useState<Record<string, string>>(() => {
    const stored = (membership?.config?.hard_caps ?? {}) as Record<string, number>;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(stored)) out[k] = String(v);
    return out;
  });
  function submit(e: React.FormEvent) {
    e.preventDefault();
    // For an existing tier we merge into its current config so we never clobber
    // the tier's other per-plan rates (computation, sign_up_*, …); for a new
    // tier we start fresh. Tier rank (membership_hierarchy) applies to both.
    const base = membership
      ? ({ ...(membership.config ?? {}) } as Record<string, unknown>)
      : ({} as Record<string, unknown>);

    const h = hierarchy.trim();
    if (h === '') {
      delete base.membership_hierarchy;
    } else {
      const n = Number(h);
      if (!Number.isNaN(n)) base.membership_hierarchy = n;
    }

    // FS-eligibility: the cashier "Activate Free Slot" action + free (FS) kits
    // can target this tier only when on. Applies to both new + edit tiers.
    if (fsEligible) base.fs_eligible = true;
    else delete base.fs_eligible;

    if (membership) {
      const caps: Record<string, number> = {};
      for (const [k, raw] of Object.entries(dailyCaps)) {
        const trimmed = raw.trim();
        if (trimmed === '') continue;
        const n = Number(trimmed);
        if (!Number.isNaN(n) && n > 0) caps[k] = n;
      }
      if (Object.keys(caps).length > 0) base.daily_caps = caps;
      else delete base.daily_caps;

      // Per-tier daily income cap groups: keep only well-formed groups (a label
      // and ≥1 plan); the limit may be 0 (= no cap) but is stored as entered.
      const groups = dailyCapGroups
        .map((g) => ({ ...g, plans: g.plans ?? [] }))
        .filter((g) => g.plans.length > 0);
      if (groups.length > 0) base.daily_cap_groups = groups;
      else delete base.daily_cap_groups;

      // Lifetime hard caps: only positive values persist; blank/0 = no cap.
      const hc: Record<string, number> = {};
      for (const [k, raw] of Object.entries(hardCaps)) {
        const trimmed = raw.trim();
        if (trimmed === '') continue;
        const n = Number(trimmed);
        if (!Number.isNaN(n) && n > 0) hc[k] = n;
      }
      if (Object.keys(hc).length > 0) base.hard_caps = hc;
      else delete base.hard_caps;
    }

    const config = Object.keys(base).length > 0 ? base : undefined;
    onSubmit({
      code: code.toUpperCase(),
      name,
      discountPct: discountPct.trim(),
      config,
    });
  }

  const errMsg = (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;

  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-3">
      <h3 className="text-base font-semibold leading-none tracking-tight">
        {membership ? 'Edit tier' : 'New tier'}
      </h3>
      <div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Code</label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={!!membership}
              placeholder="BRONZE, ELITE_PACKAGE, …"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="text-xs text-muted-foreground">Tier rank (hierarchy)</label>
            <Input
              type="number"
              min={1}
              step={1}
              value={hierarchy}
              onChange={(e) => setHierarchy(e.target.value)}
              placeholder="e.g. 1, 2, 3 (higher = senior tier)"
            />
            <p className="text-[10px] text-muted-foreground">
              Higher rank = senior tier. Required for slot upgrades: a slot can only upgrade to a
              higher-ranked tier.
            </p>
          </div>
          <div className="space-y-1 md:col-span-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={fsEligible}
                onChange={(e) => setFsEligible(e.target.checked)}
              />
              FS-eligible (free-slot tier)
            </label>
            <p className="text-[10px] text-muted-foreground">
              When on, a cashier with the “Activate Free Slot” action can create a free (FS)
              slot at this tier, and free (FS) kits may target it.
            </p>
          </div>
          {membership && (
            <div className="md:col-span-2 space-y-2 pt-2 border-t">
              <label className="text-xs font-semibold text-muted-foreground">
                Daily cap overrides
              </label>
              <p className="text-xs text-muted-foreground">
                Per-plan max cash income/day for this tier's receivers. Overrides
                the global cap (Settings). Blank = use global / no limit. Binary
                self-caps and is omitted.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {SUPPORTED_PLAN_CODES.filter((c) => c !== 'binary').map((c) => (
                  <div key={c} className="space-y-1">
                    <label className="text-[10px] text-muted-foreground">
                      {planDisplayName(c)}
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      className="h-7 text-xs"
                      placeholder=""
                      value={dailyCaps[c] ?? ''}
                      onChange={(e) =>
                        setDailyCaps((prev) => ({ ...prev, [c]: e.target.value }))
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="space-y-2 pt-3 border-t">
                <label className="text-xs font-semibold text-muted-foreground">
                  Daily income cap groups
                </label>
                <p className="text-xs text-muted-foreground">
                  Combine several plans under ONE shared daily cash-income limit for this tier's
                  earners. Income is summed across a group's plans each day; once the combined total
                  reaches the limit the excess flushes. Composes with the per-plan caps above (the
                  tighter wins). A plan may belong to at most one group. 0 / blank limit = no cap.
                </p>
                {dailyCapGroups.map((g, gi) => {
                  const usedElsewhere = new Set(
                    dailyCapGroups.filter((_, i) => i !== gi).flatMap((x) => x.plans ?? []),
                  );
                  const updateGroup = (next: Partial<DailyCapGroup>) =>
                    setDailyCapGroups((prev) => prev.map((x, i) => (i === gi ? { ...x, ...next } : x)));
                  return (
                    <div key={g.id} className="space-y-2 rounded-md border p-3">
                      <div className="flex items-center gap-2">
                        <Input
                          className="h-8 max-w-xs text-sm"
                          placeholder="Group name (e.g. Sales bonuses)"
                          value={g.label}
                          onChange={(e) => updateGroup({ label: e.target.value })}
                        />
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          className="h-8 w-32 text-xs"
                          placeholder="Daily limit"
                          value={g.limit || ''}
                          onChange={(e) => updateGroup({ limit: Number(e.target.value) || 0 })}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setDailyCapGroups((prev) => prev.filter((_, i) => i !== gi))
                          }
                        >
                          Remove
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {SUPPORTED_PLAN_CODES.map((pc) => {
                          const checked = (g.plans ?? []).includes(pc);
                          const blocked = !checked && usedElsewhere.has(pc);
                          return (
                            <label
                              key={pc}
                              className={`flex items-center gap-1.5 text-xs ${blocked ? 'opacity-40' : ''}`}
                              title={blocked ? 'Already in another group' : undefined}
                            >
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5"
                                checked={checked}
                                disabled={blocked}
                                onChange={(e) => {
                                  const plans = new Set(g.plans ?? []);
                                  if (e.target.checked) plans.add(pc);
                                  else plans.delete(pc);
                                  updateGroup({ plans: Array.from(plans) });
                                }}
                              />
                              {planDisplayName(pc)}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setDailyCapGroups((prev) => [
                      ...prev,
                      { id: crypto.randomUUID(), label: '', limit: 0, plans: [] },
                    ])
                  }
                >
                  Add cap group
                </Button>
              </div>
              <div className="space-y-2 pt-3 border-t">
                <label className="text-xs font-semibold text-muted-foreground">
                  Lifetime hard caps
                </label>
                <p className="text-xs text-muted-foreground">
                  Lifetime max cash a slot at this tier can earn from each plan (imported legacy
                  earnings count). At the cap the slot earns 0 from that plan; the boundary payout
                  partial-pays so the total lands exactly on the cap. Raise the cap to resume
                  earning. Blank / 0 = no cap. GC and other non-cash credits are never capped.
                  Binary and Binary Mentor&apos;s Bonus are separate pools: Binary caps
                  pairing/milestone cash only, Binary Mentor&apos;s Bonus caps the mentor rider
                  only.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {HARD_CAP_PLAN_CODES.map((c) => (
                    <div key={c} className="space-y-1">
                      <label className="text-[10px] text-muted-foreground">
                        {planDisplayName(c)}
                      </label>
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        className="h-7 text-xs"
                        placeholder=""
                        value={hardCaps[c] ?? ''}
                        onChange={(e) =>
                          setHardCaps((prev) => ({ ...prev, [c]: e.target.value }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {errMsg && <p className="md:col-span-2 text-sm text-destructive">{errMsg}</p>}
          <div className="md:col-span-2 flex gap-2 pt-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : membership ? 'Save' : 'Create'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
